<script>
  import { supabase } from '../../lib/supabase.js';
  import { showToast } from '../../lib/stores/toast.js';

  let { pageId, workspaceId, onclose } = $props();

  let existingProcesses = $state([]);
  let mode = $state('list');
  let editingId = $state(null);
  let processName = $state('');
  let steps = $state(['']);
  let saving = $state(false);

  async function load() {
    const { data } = await supabase
      .from('bb_tech_processes')
      .select('id, name, steps')
      .eq('page_id', pageId)
      .is('deleted_at', null);
    existingProcesses = data || [];
    if (existingProcesses.length === 0) startNew();
  }

  function startNew() {
    mode = 'edit';
    editingId = null;
    processName = '';
    steps = [''];
  }

  function loadExisting(p) {
    mode = 'edit';
    editingId = p.id;
    processName = p.name || '';
    steps = (p.steps && p.steps.length > 0) ? [...p.steps] : [''];
  }

  function onStepKey(e, idx) {
    if (e.key === 'Enter') {
      e.preventDefault();
      steps = [...steps.slice(0, idx + 1), '', ...steps.slice(idx + 1)];
      setTimeout(() => document.querySelector(`[data-step="${idx + 1}"]`)?.focus(), 0);
    } else if (e.key === 'Backspace' && steps[idx] === '' && steps.length > 1) {
      e.preventDefault();
      steps = steps.filter((_, i) => i !== idx);
      setTimeout(() => document.querySelector(`[data-step="${Math.max(0, idx - 1)}"]`)?.focus(), 0);
    }
  }

  async function save() {
    saving = true;
    try {
      const cleanSteps = steps.map(s => s.trim()).filter(Boolean);
      if (cleanSteps.length === 0) { showToast('Add at least one step', 'error'); return; }
      if (editingId) {
        await supabase.from('bb_tech_processes').update({
          name: processName.trim() || null,
          steps: cleanSteps
        }).eq('id', editingId);
      } else {
        await supabase.from('bb_tech_processes').insert({
          workspace_id: workspaceId,
          page_id: pageId,
          name: processName.trim() || null,
          steps: cleanSteps
        });
      }
      showToast('Process saved', 'success');
      onclose?.();
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally { saving = false; }
  }

  $effect(() => { load(); });
</script>

<div class="pp-overlay" onclick={onclose}>
  <div class="pp-modal" onclick={(e) => e.stopPropagation()}>
    <header class="pp-header">
      <h4>Process</h4>
      <button class="pp-x" onclick={onclose} type="button">×</button>
    </header>
    {#if mode === 'list'}
      <div class="pp-body">
        {#each existingProcesses as p}
          <button class="pp-existing" onclick={() => loadExisting(p)} type="button">
            {p.name || 'Unnamed process'} <span class="pp-step-count">{(p.steps || []).length} steps</span>
          </button>
        {/each}
        <button class="pp-new" onclick={startNew} type="button">+ New process</button>
      </div>
    {:else}
      <div class="pp-body">
        <input
          class="pp-name"
          bind:value={processName}
          placeholder="Process name (optional)"
        />
        {#each steps as step, idx}
          <input
            class="pp-step"
            data-step={idx}
            bind:value={steps[idx]}
            onkeydown={(e) => onStepKey(e, idx)}
            placeholder="Step {idx + 1}"
          />
        {/each}
      </div>
    {/if}
    <footer class="pp-footer">
      <button class="pp-secondary" onclick={onclose} type="button">Cancel</button>
      {#if mode === 'edit'}
        <button class="pp-primary" onclick={save} disabled={saving} type="button">{saving ? 'Saving…' : 'Done'}</button>
      {/if}
    </footer>
  </div>
</div>

<style>
  .pp-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 250;
  }
  .pp-modal {
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 12px;
    width: 480px; max-width: 95vw; max-height: 80vh;
    display: flex; flex-direction: column;
  }
  .pp-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .pp-header h4 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 16px; color: #f0efe9; }
  .pp-x {
    background: rgba(255,255,255,0.06);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 26px; height: 26px;
    border-radius: 50%; cursor: pointer; font-size: 14px;
  }
  .pp-body { padding: 14px 16px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
  .pp-existing {
    text-align: left; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    color: #d8d8dc;
    padding: 8px 12px; border-radius: 8px;
    cursor: pointer; font-size: 13px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .pp-existing:hover { background: rgba(212,175,55,0.08); border-color: rgba(212,175,55,0.3); }
  .pp-step-count { color: #9aa0a6; font-size: 10px; font-family: 'JetBrains Mono', monospace; }
  .pp-new {
    background: rgba(212,175,55,0.08);
    border: 1px dashed rgba(212,175,55,0.3);
    color: #d4af37;
    padding: 8px 12px; border-radius: 8px;
    cursor: pointer; font-size: 12px;
  }
  .pp-name, .pp-step {
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
  }
  .pp-name:focus, .pp-step:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .pp-name { margin-bottom: 8px; }
  .pp-footer {
    display: flex; gap: 8px; justify-content: flex-end;
    padding: 10px 16px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .pp-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c; border: none;
    padding: 7px 14px; border-radius: 6px;
    cursor: pointer; font-weight: 600; font-size: 12px;
  }
  .pp-secondary {
    background: rgba(255,255,255,0.05);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 7px 12px; border-radius: 6px;
    cursor: pointer; font-size: 12px;
  }
</style>
